import FilterRow from '@components/Common/FilterRow';
import { Panel } from '@components/Common/Panel';
import KpiTile from '@components/Common/KpiTile';
import { compactAmount, fmtMoney } from '@helpers/money';
import ActionIconButton from '@components/Common/ActionIconButton';
import ActivityMonitor from './ActivityMonitor';
import CandidatesByCity from './CandidatesByCity';
import ReferralInsights from './ReferralInsights';
import ErrorMessage from '@components/Common/ErrorMessage';
import EmptyState from '@components/Common/EmptyState';
import AutocompleteFormField from '@components/Common/AutocompleteFormField';
import FilterBar from '@components/Common/FilterBar';
import MetricTable from '@components/Common/MetricTable';
import MintlyTable from '@components/Common/Table';
import DashboardSubtitle from './DashboardHero';
import PageHeader, { PageHeroSlot, usePageHeaderSlots } from '@components/Common/PageHeader';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import DashboardActions from '@components/Dashboard/DashboardActions';
import Dashboard from '@components/Dashboard';
import { chartColor } from '@components/Common/Charts/chartPalette';
import { useThemeMode } from '../../theme/ThemeModeProvider';
import CRMTabs from '@components/Common/CRMTabs';
import EmailAnalytics from '@components/EmailAnalytics';
import SelectFormField from '@components/Common/SelectFormField';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import { canSeeRevenue, isHrLead, isMarketingRole, isSuperAdmin, useCurrentUserId, useCurrentUserRole } from '@components/Common/Utils/roles';
import {
  AnalyticsFilter,
  employers as employersQuery,
  getActiveValuesByTypeQuery,
  getAllUsersForNames,
  getCandidateQualityQuery,
  getHiringFunnelQuery,
  getJobPerformanceQuery,
  getPipelineHealthQuery,
  getRecruiterProductivityQuery,
  getRevenueReportQuery,
  getPlacementFeesQuery,
} from '@constants/graphQLQueries';
import { FALLBACK_INDUSTRIES } from '@constants/jobMetadataConstants';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { EXPERIENCE_LEVELS, JOB_TYPES } from '@constants/jobEnums';
import { getSubmissionActivityQuery, getSubmissionDetailsQuery } from '@constants/graphQLQueries';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import { statusSummary } from '@constants/applicationStatus';
import axios from 'axios';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import HourglassBottomOutlinedIcon from '@mui/icons-material/HourglassBottomOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import {
  Box,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import './index.scss';
import { COUNT_Y_AXIS, linearSeries } from '@components/Common/Charts/chartAxes';
import { formatDate, isoDaysAgo } from '@helpers/date';

interface HiringFunnel {
  jobsPosted: number;
  applicants: number;
  /** The recruiter half of the funnel, read from each record's history. */
  sourced: number;
  contacted: number;
  interested: number;
  qualified: number;
  submitted: number;
  contactRate: number;
  interestRate: number;
  qualificationRate: number;
  submissionToInterviewRate: number;
  placementRate: number;
  /** Null until there is something to average. */
  avgDaysToFirstContact: number | null;
  avgDaysToSubmit: number | null;
  avgDaysToFill: number | null;
  shortlisted: number;
  interviews: number;
  offers: number;
  accepted: number;
  joined: number;
  applyToShortlistRate: number;
  shortlistToInterviewRate: number;
  interviewToOfferRate: number;
  offerToAcceptRate: number;
  offerToJoinRate: number;
  series: { period: string; applicants: number; joined: number }[];
  scope: string;
}

interface PipelineHealth {
  openJobs: number;
  filledJobs: number;
  jobsWithZeroApplicants: number;
  rolesOpenOver30Days: number;
  stuckCandidates: number;
  stageCounts: { stage: string; count: number }[];
  scope: string;
}

interface RecruiterStats {
  recruiterId: string;
  jobsHandled: number;
  candidatesHandled: number;
  interviews: number;
  offersMade: number;
  hires: number;
  creditedHires: number;
  avgDaysToShortlist: number;
  avgDaysToHire: number;
  offerToJoinRate: number;
}

interface JobPerf {
  jobId: string;
  title: string | null;
  companyName: string | null;
  views: number;
  applications: number;
  applyRate: number;
  shortlistRate: number;
  dropOffRate: number;
  hires: number;
  daysToFirstApplicant: number;
}

interface JobPerformanceReport {
  totalViews: number;
  totalApplications: number;
  avgApplyRate: number;
  avgDaysToFirstApplicant: number;
  jobsCovered: number;
  jobs: JobPerf[];
  scope: string;
}

interface SegmentQuality {
  segment: string;
  candidates: number;
  interviews: number;
  hires: number;
  interviewToHireRate: number;
  shortlistRate: number;
}

interface CandidateQuality {
  totalCandidates: number;
  interviewed: number;
  hired: number;
  interviewToHireRate: number;
  evaluatedCandidates: number;
  strongMatchCandidates: number;
  avgMatchScore: number;
  interviewsRecorded: number;
  interviewNoShowRate: number;
  segmentBy: string;
  segments: SegmentQuality[];
  rejectionReasons: { reason: string; count: number }[];
  scope: string;
}

interface RevenueReport {
  currency: string | null;
  totalRevenue: number;
  transactions: number;
  payingEmployers: number;
  totalEmployers: number;
  freeToPaidRate: number;
  availableCurrencies: string[];
  scope: string;
  byPlan: { planType: string; revenue: number; count: number }[];
  trend: { period: string; revenue: number }[];
  topEmployers: { employerId: string | null; companyName: string | null; revenue: number }[];
  planRevenue: number | null;
  placementRevenue: number | null;
  placements: number | null;
  byHr: HrRevenueRow[] | null;
}

interface HrRevenueRow {
  hrUserId: string;
  planRevenue: number;
  placementRevenue: number;
  placements: number;
  total: number;
}

interface PlacementFeeRow {
  _id: string;
  jobTitle: string | null;
  hrUserId: string | null;
  basis: string;
  percent: number | null;
  amount: number;
  currency: string;
  hiredAt: string | null;
}

type TabKey =
  | 'overview'
  | 'funnel'
  | 'pipeline'
  | 'recruiter'
  | 'submissions'
  | 'jobs'
  | 'quality'
  | 'revenue'
  | 'alerts'
  | 'cities'
  | 'activity'
  | 'email'
  | 'referrals';

const ALL_TABS: TabKey[] = [
  'overview',
  'funnel',
  'pipeline',
  'recruiter',
  'submissions',
  'jobs',
  'quality',
  'revenue',
  'alerts',
  'cities',
  'activity',
  'email',
  'referrals',
];
type SegmentBy = 'industry' | 'experience' | 'city';

const PERIODS = [
  { label: '30 days', days: 30, granularity: 'week' as const },
  { label: '90 days', days: 90, granularity: 'week' as const },
  { label: '12 months', days: 365, granularity: 'month' as const },
];

/*
 * Bounded filter option lists. Job type comes from the shared constant rather than a fourth copy:
 * the comment here used to say "mirror the job create/edit forms", which is a maintenance
 * instruction that only worked while someone remembered it. This copy had already drifted --
 * it was missing INTERNSHIP, so internships could not be filtered for at all.
 */
const JOB_TYPE_OPTIONS = JOB_TYPES;
const EXPERIENCE_OPTIONS = EXPERIENCE_LEVELS;

/** How a fee's terms read in a table. FIXED / PERCENT_OF_CTC are fees recorded before the salary terms. */
const feeTerms = (r: { basis: string; percent?: number | null }) =>
  r.basis === 'ONE_MONTH' ? 'One month' : r.basis === 'HALF_MONTH' ? 'Half month'
    : r.basis === 'PERCENT_OF_CTC' ? `${r.percent ?? '-'}% of CTC` : 'Fixed';

/*
 * An unknown figure reads as unknown, not as zero. "0 hires" and "we could not work it out" look
 * identical on a tile and mean opposite things; the dashboard already takes that care with the
 * time metrics and the stage counts, and these two formatters were quietly undoing it by
 * coercing null to 0. A real zero still prints as 0.
 */
const UNKNOWN = '-';
const pct = (v?: number | null) => (v == null ? UNKNOWN : `${v.toFixed(1)}%`);
const num = (v?: number | null) => (v == null ? UNKNOWN : v.toLocaleString());
/* A mean with nothing to average is unknown, not zero days: "0 days to fill" is a claim. */
const days = (v?: number | null) => (v == null ? '\u2014' : `${v.toFixed(1)}d`);


const Analytics = () => {
  /*
   * Subscribed for the re-render, not for the value.
   *
   * `chartColor` resolves the tokens through getComputedStyle, which is what keeps chart series on
   * the palette instead of on drifting literals -- but it resolves at CALL time, and this page
   * renders once and sits there. Without a reason to render again, a theme switch recolours the
   * entire console except its charts.
   */
  useThemeMode();

  const role = useCurrentUserRole();
  const currentUserId = useCurrentUserId();
  const superAdmin = isSuperAdmin(role);
  /*
   * Marketing has no clients of its own, so the backend answers it platform-wide (it scopes only
   * an HR): the personal "My ..." labels would be wrong for it. Revenue is refused to Marketing on
   * the proxy and the backend, so its tab is not offered.
   */
  const marketing = isMarketingRole(role);
  /*
   * An HR Lead (owner, 2026-11-03) has HR's tabs answered team-wide, plus Marketing's to read, and
   * no revenue: a team-wide revenue figure is the whole team's book. The backend scopes her the
   * same way; the labels follow it.
   */
  const hrLead = isHrLead(role);
  const platformWide = superAdmin || marketing || hrLead;
  const revenueVisible = canSeeRevenue(role);
  // The recruiter comparison: every recruiter's row, named. A Super Admin's and an HR Lead's.
  const teamView = superAdmin || hrLead;

  // Tab lives in the URL: /dashboard (Overview) and /dashboard/<tab> for the rest,
  // so tabs are linkable, bookmarkable, and survive refresh/back.
  const navigate = useNavigate();
  const { tab: tabParam } = useParams<{ tab?: string }>();
  const requested = (tabParam as TabKey) ?? 'overview';
  /*
   * Marketing's tabs (owner, 2026-10-19): what bears on sign-ups and promotion. The recruiter and
   * ops views (funnel, pipeline, leaderboard, submissions, candidate quality, alerts) are HR's work,
   * and revenue is refused to Marketing outright. Referrals is the programme's totals, read only.
   */
  const MARKETING_TABS: TabKey[] = ['overview', 'jobs', 'cities', 'referrals'];
  const allowedFor = (t: TabKey) => {
    if (marketing) return MARKETING_TABS.includes(t);
    if (t === 'revenue') return revenueVisible;
    if (t === 'referrals') return superAdmin || hrLead;
    return (t !== 'email' && t !== 'activity') || superAdmin;
  };
  const tab: TabKey = ALL_TABS.includes(requested) && allowedFor(requested) ? requested : 'overview';
  const setTab = (next: TabKey) =>
    navigate(next === 'overview' ? '/dashboard' : `/dashboard/${next}`);
  const headerSlots = usePageHeaderSlots();

  // Visible tabs (Revenue is Super-Admin only). Drives the shared CRMTabs bar; the index
  // it reports maps back to a TabKey for URL-based routing.
  const allTabs: { key: TabKey; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'funnel', label: 'Hiring Funnel' },
    { key: 'pipeline', label: 'Pipeline Health' },
    { key: 'recruiter', label: platformWide ? 'Recruiter Leaderboard' : 'My Scorecard' },
    { key: 'submissions', label: platformWide ? 'Submissions' : 'My Submissions' },
    { key: 'jobs', label: 'Job Performance' },
    { key: 'quality', label: 'Candidate Quality' },
    // Every staff member: an HR sees their own clients' plans and their own placement fees,
    // scoped by the backend. Email stays Super Admin only.
    ...(revenueVisible ? [{ key: 'revenue' as TabKey, label: superAdmin ? 'Revenue / ROI' : 'My revenue' }] : []),
    { key: 'alerts', label: 'Alerts & Risks' },
    { key: 'cities', label: 'Candidates By City' },
    // `email` was in ALL_TABS and super-admin gated, but missing here — so findIndex returned -1,
    // Math.max(0, -1) picked 0, and the bar highlighted Overview while the Email panel rendered.
    // The tab was reachable only through the /email-analytics redirect, which is why it went
    // unnoticed: nobody could click their way to it.
    ...(superAdmin ? [{ key: 'email' as TabKey, label: 'Email' }] : []),
    // Every person's actions, staff included: Super Admin only, as the backend enforces.
    ...(superAdmin ? [{ key: 'activity' as TabKey, label: 'Activity' }] : []),
    // Programme-wide referral metrics: platform totals and the top-referrer leaderboard, so
    // Super Admin only like the payouts it summarises.
    ...(platformWide ? [{ key: 'referrals' as TabKey, label: 'Referrals' }] : []),
  ];
  const visibleTabs = allTabs.filter((t) => allowedFor(t.key));

  const activeTabIndex = Math.max(0, visibleTabs.findIndex((t) => t.key === tab));

  // Clean an unknown /dashboard/<garbage> slug back to the Overview URL.
  useEffect(() => {
    if (tabParam && !ALL_TABS.includes(tabParam as TabKey)) {
      navigate('/dashboard', { replace: true });
    }
  }, [tabParam, navigate]);
  const [periodIdx, setPeriodIdx] = useState(1); // default 90 days
  const [granularity, setGranularity] = useState<'week' | 'month' | 'quarter'>('week');
  const [segmentBy, setSegmentBy] = useState<SegmentBy>('industry');
  const [currency, setCurrency] = useState<string>('');

  // Dimension filters: a draft the user edits, and the applied set that drives fetches.
  const emptyFilters = { employerId: '', industry: '', jobType: '', experience: '', city: '' };
  const [draft, setDraft] = useState(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);

  // Dropdown option sources for the dimension filters.
  const [industryOptions, setIndustryOptions] = useState<string[]>([]);
  const [employerOptions, setEmployerOptions] = useState<{ id: string; label: string }[]>([]);
  // City suggestions from AWS Location Services (via the /api/locationService proxy).
  const [cityOptions, setCityOptions] = useState<string[]>([]);
  const cityTimer = useMemo(() => ({ current: null as ReturnType<typeof setTimeout> | null }), []);

  const searchCities = useCallback(
    (query: string) => {
      if (cityTimer.current) clearTimeout(cityTimer.current);
      if (!query || query.trim().length < 2) {
        setCityOptions([]);
        return;
      }
      cityTimer.current = setTimeout(() => {
        axios
          .post('/api/locationService', { query: query.trim(), type: 'city', maxCount: 10 })
          .then((res) => setCityOptions((res.data as string[]) ?? []))
          .catch(() => setCityOptions([]));
      }, 400);
    },
    [cityTimer],
  );

  // Load the active industry list (falls back to the shared constant if the metadata call fails).
  useEffect(() => {
    let active = true;
    fetchGraphQLApiData(getActiveValuesByTypeQuery('INDUSTRY'))
      .then((res) => {
        if (!active) return;
        const list: string[] = res?.data?.getActiveValuesByType ?? [];
        setIndustryOptions(list.length ? list : FALLBACK_INDUSTRIES);
      })
      .catch(() => {
        if (active) setIndustryOptions(FALLBACK_INDUSTRIES);
      });
    return () => {
      active = false;
    };
  }, []);

  // Super Admins can filter by a specific employer — load a searchable name→id list.
  useEffect(() => {
    if (!superAdmin) return;
    let active = true;
    fetchGraphQLApiData(employersQuery(0, 200))
      .then((res) => {
        if (!active) return;
        const rows: any[] = res?.data?.getAllEmployers?.content ?? [];
        setEmployerOptions(
          rows.filter(Boolean).map((e) => ({
            id: e._id,
            label:
              e.personalDetails?.fullName ||
              `${e.personalDetails?.firstName ?? ''} ${e.personalDetails?.lastName ?? ''}`.trim() ||
              e.personalDetails?.email ||
              e._id,
          })),
        );
      })
      .catch(() => {
        /* non-fatal: the employer filter simply stays empty */
      });
    return () => {
      active = false;
    };
  }, [superAdmin]);

  const [funnel, setFunnel] = useState<HiringFunnel | null>(null);
  const [pipeline, setPipeline] = useState<PipelineHealth | null>(null);
  const [recruiters, setRecruiters] = useState<RecruiterStats[] | null>(null);

  // ── Submissions ──
  interface SubmissionDayCount { day: string; count: number }
  interface SubmissionRow { recruiterId: string; total: number; byDay: SubmissionDayCount[] }
  interface SubmissionActivity {
    from: string; to: string; days: string[]; rows: SubmissionRow[];
    totalSubmissions: number; unattributedSubmissions: number; scope: string;
  }
  interface SubmissionRecord {
    applicationId: string; jobSeekerId: string; candidateName?: string | null;
    jobId: string; jobTitle?: string | null; companyName?: string | null;
    status: string; submittedAt: string; submittedBy: string; resubmission: boolean;
  }
  const [submissions, setSubmissions] = useState<SubmissionActivity | null>(null);
  const [submissionDays, setSubmissionDays] = useState(14);
  const [drillRows, setDrillRows] = useState<SubmissionRecord[] | null>(null);
  const [drillLoading, setDrillLoading] = useState(false);
  /*
   * The selected cell lives in the URL, not in state.
   *
   * "Who did Priya submit on Tuesday" then becomes a link somebody can paste into Slack, and it
   * survives a reload. Same reasoning as the workspace tab params.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const drillHr = searchParams.get('hr') || '';
  const drillDay = searchParams.get('day') || '';

  const selectCell = (recruiterId: string, day: string) => {
    const next = new URLSearchParams(searchParams);
    if (drillHr === recruiterId && drillDay === day) {
      // Clicking the selected cell again closes the panel.
      next.delete('hr');
      next.delete('day');
    } else {
      next.set('hr', recruiterId);
      next.set('day', day);
    }
    setSearchParams(next, { replace: true });
  };
  const [jobsReport, setJobsReport] = useState<JobPerformanceReport | null>(null);
  const [quality, setQuality] = useState<CandidateQuality | null>(null);
  const [revenue, setRevenue] = useState<RevenueReport | null>(null);
  const [fees, setFees] = useState<PlacementFeeRow[]>([]);
  const [nameMap, setNameMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const filter: AnalyticsFilter = useMemo(() => {
    const p = PERIODS[periodIdx];
    return {
      from: isoDaysAgo(p.days),
      to: isoDaysAgo(0),
      granularity,
      employerId: appliedFilters.employerId || undefined,
      industry: appliedFilters.industry || undefined,
      jobType: appliedFilters.jobType || undefined,
      experience: appliedFilters.experience || undefined,
      city: appliedFilters.city || undefined,
      segmentBy,
      currency: currency || undefined,
    };
  }, [periodIdx, granularity, appliedFilters, segmentBy, currency]);

  const load = useCallback(async () => {
    // Overview and Candidates-by-city fetch their own data; no analytics query needed here.
    if (tab === 'overview' || tab === 'cities' || tab === 'activity' || tab === 'referrals') return;
    setLoading(true);
    setError('');
    try {
      if (tab === 'funnel') {
        const res = await fetchGraphQLApiData(getHiringFunnelQuery(filter));
        setFunnel(res?.data?.getHiringFunnel ?? null);
      } else if (tab === 'pipeline' || tab === 'alerts') {
        const res = await fetchGraphQLApiData(getPipelineHealthQuery(filter));
        setPipeline(res?.data?.getPipelineHealth ?? null);
      } else if (tab === 'recruiter') {
        const res = await fetchGraphQLApiData(getRecruiterProductivityQuery(filter));
        setRecruiters(res?.data?.getRecruiterProductivity ?? []);
      } else if (tab === 'submissions') {
        /*
         * Its own day range, not PERIODS. The shortest preset there is 30 days at week
         * granularity, and this matrix is always daily -- a 90-day one is 90 columns.
         */
        const res = await fetchGraphQLApiData(
          getSubmissionActivityQuery({ ...filter, from: isoDaysAgo(submissionDays), to: undefined }),
        );
        setSubmissions(res?.data?.getSubmissionActivity ?? null);
      } else if (tab === 'jobs') {
        const res = await fetchGraphQLApiData(getJobPerformanceQuery(filter));
        setJobsReport(res?.data?.getJobPerformance ?? null);
      } else if (tab === 'quality') {
        const res = await fetchGraphQLApiData(getCandidateQualityQuery(filter));
        setQuality(res?.data?.getCandidateQuality ?? null);
      } else if (tab === 'email') {
        // EmailAnalytics fetches its own data. Without this branch the bare `else` below ran, so
        // sitting on the Email tab fired a Revenue report query on mount and on every filter
        // change, and the hero Refresh button refetched revenue while the reader watched email.
        return;
      } else if (tab === 'revenue') {
        // Explicit, not a bare `else`: the bare else fetched revenue for any tab without its own
        // branch, which is how the Candidates-by-city tab came to fire a revenue query (refused
        // for an HR, so the page showed an error) on every visit.
        const res = await fetchGraphQLApiData(getRevenueReportQuery(filter));
        const rep: RevenueReport | null = res?.data?.getRevenueReport ?? null;
        setRevenue(rep);
        // The recent fees list; the backend scopes an HR to their own.
        fetchGraphQLApiData(getPlacementFeesQuery())
          .then((r) => setFees(r?.data?.getPlacementFees ?? []))
          .catch(() => setFees([]));
        // Adopt the currency the backend chose so the toggle reflects it.
        if (rep?.currency && !currency) setCurrency(rep.currency);
      }
    } catch (e: any) {
      setError(e?.message || 'Could not load analytics.');
    } finally {
      setLoading(false);
    }
  }, [tab, filter, currency]);

  useEffect(() => {
    load();
  }, [load]);

  // Resolve recruiter ids → display names once, for the team leaderboard.
  useEffect(() => {
    if (!teamView) return;
    let active = true;
    fetchGraphQLApiData(getAllUsersForNames)
      .then((users) => {
        if (!active) return;
        const map: Record<string, string> = {};
        (users?.data?.getAllUsers?.content ?? []).forEach((u: any) => {
          const name =
            u?.personalDetails?.fullName ||
            [u?.personalDetails?.firstName, u?.personalDetails?.lastName].filter(Boolean).join(' ');
          if (u?._id && name) map[u._id] = name;
        });
        setNameMap(map);
      })
      .catch(() => {
        /* non-fatal: fall back to showing ids */
      });
    return () => {
      active = false;
    };
  }, [teamView]);

  /* Load the candidates behind the selected cell. Driven by the URL, so a pasted link works. */
  useEffect(() => {
    if (tab !== 'submissions' || !drillHr || !drillDay) {
      setDrillRows(null);
      return;
    }
    let active = true;
    setDrillLoading(true);
    fetchGraphQLApiData(
      getSubmissionDetailsQuery(
        { ...filter, from: isoDaysAgo(submissionDays), to: undefined },
        drillHr,
        drillDay,
      ),
    )
      .then((res) => {
        if (active) setDrillRows(res?.data?.getSubmissionDetails ?? []);
      })
      .catch(() => {
        if (active) setDrillRows([]);
      })
      .finally(() => {
        if (active) setDrillLoading(false);
      });
    return () => {
      active = false;
    };
  }, [tab, drillHr, drillDay, filter, submissionDays]);

  const recruiterName = (id: string) =>
    nameMap[id] || (id === currentUserId ? 'You' : id);

  const applyFilters = () => setAppliedFilters(draft);
  const clearFilters = () => {
    setDraft(emptyFilters);
    setAppliedFilters(emptyFilters);
  };

  /** Drop one filter without opening the sheet — chip, then refetch. */
  const clearOne = (key: keyof typeof emptyFilters) => {
    const next = { ...appliedFilters, [key]: '' };
    setDraft(next);
    setAppliedFilters(next);
  };

  /**
   * What is applied right now, labelled for the chips. Reads off
   * appliedFilters rather than the draft: the chips describe the data on
   * screen, not what someone has half-typed into the sheet.
   */
  const appliedSummary = useMemo(() => {
    const labelFor: Record<string, (v: string) => string> = {
      industry:   (v) => `Industry: ${v}`,
      city:       (v) => `City: ${v}`,
      jobType:    (v) => `Job type: ${JOB_TYPE_OPTIONS.find((o) => o.value === v)?.label ?? v}`,
      experience: (v) => `Experience: ${v}`,
      employerId: (v) => `Employer: ${employerOptions.find((o) => o.id === v)?.label ?? v}`,
    };
    return (Object.keys(emptyFilters) as (keyof typeof emptyFilters)[])
      .filter((key) => appliedFilters[key])
      .map((key) => ({
        key,
        label: labelFor[key](appliedFilters[key]),
        onClear: () => clearOne(key),
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, employerOptions]);

  /*
   * The recruiter funnel (specification section 7). Sourcing and Contacted count only candidates
   * we found, so this chart is theirs; the client-facing stages below start at Submitted.
   */
  const sourcingStages = funnel
    ? [
        { label: 'Sourcing', value: funnel.sourced },
        { label: 'Contacted', value: funnel.contacted },
        { label: 'Interested', value: funnel.interested },
        { label: 'Qualified', value: funnel.qualified },
        { label: 'Submitted', value: funnel.submitted },
      ]
    : [];

  const funnelStages = funnel
    ? [
        { label: 'Applied', value: funnel.applicants },
        { label: 'Shortlisted', value: funnel.shortlisted },
        { label: 'Interview', value: funnel.interviews },
        { label: 'Offer', value: funnel.offers },
        { label: 'Accepted', value: funnel.accepted },
        { label: 'Joined', value: funnel.joined },
      ]
    : [];

  const conversions = funnel
    ? [
        // The recruiter's own rates first: each is measured against the candidates we sourced.
        { label: 'Sourced → Contacted', value: funnel.contactRate },
        { label: 'Contacted → Interested', value: funnel.interestRate },
        { label: 'Interested → Qualified', value: funnel.qualificationRate },
        { label: 'Submitted → Interview', value: funnel.submissionToInterviewRate },
        { label: 'Submitted → Joined', value: funnel.placementRate },
        { label: 'Apply → Shortlist', value: funnel.applyToShortlistRate },
        { label: 'Shortlist → Interview', value: funnel.shortlistToInterviewRate },
        { label: 'Interview → Offer', value: funnel.interviewToOfferRate },
        { label: 'Offer → Accept', value: funnel.offerToAcceptRate },
        { label: 'Offer → Join', value: funnel.offerToJoinRate },
      ]
    : [];

  return (
    <Box className="an-page">
      {/* The page header every CC page has: H1, the active tab's sentence, its actions, and the
          tabs directly under it. This page used to open on the tab bar and a hero card. */}
      <PageHeader
        title="Dashboard"
        icon={<DashboardOutlinedIcon />}
        subtitle={<DashboardSubtitle tab={tab} superAdmin={superAdmin} />}
        slots={tab === 'email' ? headerSlots : undefined}
        actions={
          tab === 'email' ? undefined : tab === 'overview' ? (
            <DashboardActions superAdmin={superAdmin} />
          ) : (
            <ActionIconButton label="Refresh" color="primary" icon={<RefreshIcon />} onClick={load} busy={loading} />
          )
        }
        tabs={{
          value: activeTabIndex,
          onChange: (i) => setTab(visibleTabs[i].key),
          tabs: visibleTabs.map((t) => ({ label: t.label })),
        }}
      />
      <Box className="an-body max-w-container">

        {/* Overview = the platform dashboard (self-fetching). */}
        {tab === 'overview' && <Dashboard />}

        {/* Every tab past Overview leans on wide comparison tables that cannot
            be read at 390px. Overview itself — a KPI row plus one chart — is
            fine on a phone, so only the rest is gated. */}

        {/* Controls + dimension filters drive the comparison tabs only.
            Overview is a self-fetching KPI+chart that ignores them. Email renders EmailAnalytics,
            which takes no props and owns its own period state — so this bar was seven controls
            that moved nothing, plus a second period picker stacked above its own. Filters that
            do not filter are worse than absent: a Super Admin slices email performance by
            employer, the numbers do not move, and a wrong conclusion gets drawn. */}
        {/* The period and dimension filters mean nothing to the candidate pool; the City tab has its own Joined window. */}
        {tab !== 'overview' && tab !== 'email' && tab !== 'cities' && tab !== 'activity' && tab !== 'referrals' && (
          <>
        {/* ── Controls ─────────────────────────────────────────────────── */}
        <FilterRow>
          <ToggleButtonGroup
            value={periodIdx}
            exclusive
            size="small"
            onChange={(_, v) => {
              if (v !== null) {
                setPeriodIdx(v);
                setGranularity(PERIODS[v].granularity);
              }
            }}
          >
            {PERIODS.map((p, i) => (
              <ToggleButton key={p.label} value={i}>
                {p.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <ToggleButtonGroup
            value={granularity}
            exclusive
            size="small"
            onChange={(_, v) => { if (v !== null) setGranularity(v); }}
          >
            <ToggleButton value="week">Weekly</ToggleButton>
            <ToggleButton value="month">Monthly</ToggleButton>
            <ToggleButton value="quarter">Quarterly</ToggleButton>
          </ToggleButtonGroup>
        </FilterRow>

        {/* ── Dimension filters ────────────────────────────────────────── */}
        {/* Inline on a desktop; a Filters button plus chips on a phone. */}
        <FilterBar
          applied={appliedSummary}
          onApply={applyFilters}
          onClearAll={clearFilters}
          onCancel={() => setDraft(appliedFilters)}
          collapsibleOnDesktop
          storageKey="cc-dashboard-filters"
        >
            {/* The console's own field components, so this toolbar reads like every other form
                rather than like MUI's defaults. It was five controls with notched inline labels,
                which is the one label style nothing else in the console uses. */}
            <SelectFormField
              required={false}
              name="industry"
              label="Industry"
              size="small"
              value={draft.industry}
              options={[
                { value: '', label: 'All industries' },
                ...industryOptions.map((opt) => ({ value: opt, label: opt })),
              ]}
              onChange={(_n, v) => setDraft({ ...draft, industry: String(v) })}
            />
            <AutocompleteFormField
              required={false}
              freeSolo
              name="city"
              label="Location (city)"
              size="small"
              /* The three Selects beside this show their empty state as an "All …" option.
                 An Autocomplete has no option to sit on when nothing is picked, so without a
                 placeholder it is the only blank box in the row. */
              placeholder="All locations"
              options={cityOptions}
              value={draft.city}
              autocompleteProps={{ filterOptions: (x: string[]) => x }}
              onInputChange={(_e, val) => {
                setDraft({ ...draft, city: val });
                searchCities(val);
              }}
              onChange={(_e, val) => setDraft({ ...draft, city: (val as string) ?? '' })}
            />
            <SelectFormField
              required={false}
              name="jobType"
              label="Job type"
              size="small"
              value={draft.jobType}
              options={[{ value: '', label: 'All job types' }, ...JOB_TYPE_OPTIONS]}
              onChange={(_n, v) => setDraft({ ...draft, jobType: String(v) })}
            />
            <SelectFormField
              required={false}
              name="experience"
              label="Experience"
              size="small"
              value={draft.experience}
              options={[
                { value: '', label: 'All experience' },
                ...EXPERIENCE_OPTIONS,
              ]}
              onChange={(_n, v) => setDraft({ ...draft, experience: String(v) })}
            />
            {superAdmin && (
              <AutocompleteFormField
                required={false}
                name="employerId"
                label="Employer"
                size="small"
                placeholder="All employers"
                options={employerOptions}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={employerOptions.find((o) => o.id === draft.employerId) ?? null}
                onChange={(_e, val) => setDraft({ ...draft, employerId: val?.id ?? '' })}
              />
            )}
        </FilterBar>
          </>
        )}

        {loading && !funnel && !pipeline && !recruiters && !jobsReport && !quality && !revenue && <SkeletonLoader />}
        {error && (
          <Box className="an-error">
            <ErrorMessage message={error} onRetry={load} />
          </Box>
        )}

        {/* ── Hiring Funnel ────────────────────────────────────────────── */}
        {tab === 'funnel' && funnel && (
          <>
            <Box className="cc-kpi-grid">
              <KpiTile label="Jobs Posted" value={num(funnel.jobsPosted)} icon={<WorkOutlineOutlinedIcon />} tone="primary" />
              <KpiTile label="Applicants" value={num(funnel.applicants)} icon={<PeopleAltOutlinedIcon />} tone="indigo" />
              <KpiTile label="Shortlisted" value={num(funnel.shortlisted)} icon={<GroupsOutlinedIcon />} tone="teal" />
              <KpiTile label="Interviews" value={num(funnel.interviews)} icon={<GroupsOutlinedIcon />} tone="teal" />
              <KpiTile label="Offers" value={num(funnel.offers)} icon={<GroupsOutlinedIcon />} tone="amber" />
              <KpiTile label="Joined" value={num(funnel.joined)} icon={<GroupsOutlinedIcon />} tone="success" />
            </Box>

            {/* Time metrics. A mean with nothing to average reads as a dash, never as zero days. */}
            <Box className="cc-kpi-grid">
              <KpiTile label="Days to first contact" value={days(funnel.avgDaysToFirstContact)} icon={<HourglassBottomOutlinedIcon />} tone="indigo" />
              <KpiTile label="Days to submit" value={days(funnel.avgDaysToSubmit)} icon={<HourglassBottomOutlinedIcon />} tone="teal" />
              <KpiTile label="Days to fill a role" value={days(funnel.avgDaysToFill)} icon={<HourglassBottomOutlinedIcon />} tone="amber" />
            </Box>

            <Box className="an-conv-grid">
              {conversions.map((c) => (
                <Panel variant="stat" key={c.label} className="an-conv-tile">
                  <Typography className="an-conv-value">{pct(c.value)}</Typography>
                  <Typography className="an-conv-label">{c.label}</Typography>
                </Panel>
              ))}
            </Box>

            <Box className="an-charts-row">
              <Card variant="outlined" className="cc-panel cc-panel--panel an-panel">
                <CardContent>
                  <Typography variant="subtitle1" className="an-chart-title">Sourcing funnel</Typography>
                  <Typography variant="caption" className="an-chart-note">
                    Candidates we found and worked, from sourcing to submission. Someone who applied on the site
                    enters at Submitted.
                  </Typography>
                  <BarChart
                    hideLegend
                    xAxis={[{ data: sourcingStages.map((s) => s.label), scaleType: 'band', tickLabelStyle: { fontSize: 11 } }]}
                    series={[{ data: sourcingStages.map((s) => s.value), label: 'Candidates', color: chartColor('--teal') }]}
                    yAxis={COUNT_Y_AXIS}
                    height={260}
                    margin={{ top: 8, right: 16, bottom: 40, left: 44 }}
                  />
                </CardContent>
              </Card>
            </Box>

            <Box className="an-charts-row">
              <Card variant="outlined" className="cc-panel cc-panel--panel an-panel">
                <CardContent>
                  <Typography variant="subtitle1" className="an-chart-title">Funnel by stage</Typography>
                  <BarChart
                    hideLegend
                    xAxis={[{ data: funnelStages.map((s) => s.label), scaleType: 'band', tickLabelStyle: { fontSize: 11 } }]}
                    series={[{ data: funnelStages.map((s) => s.value), label: 'Candidates', color: chartColor('--acc') }]}
                    yAxis={COUNT_Y_AXIS}
                    height={280}
                    margin={{ top: 20, right: 16, bottom: 40, left: 44 }}
                  />
                </CardContent>
              </Card>

              <Card variant="outlined" className="cc-panel cc-panel--panel an-panel an-chart-half">
                <CardContent>
                  <Typography variant="subtitle1" className="an-chart-title">Applicants vs hires over time</Typography>
                  {funnel.series.length > 0 ? (
                    <LineChart
                      xAxis={[{ data: funnel.series.map((p) => p.period), scaleType: 'band', tickLabelStyle: { fontSize: 11 } }]}
                      series={linearSeries([
                        { data: funnel.series.map((p) => p.applicants), label: 'Applicants', color: chartColor('--acc') },
                        { data: funnel.series.map((p) => p.joined), label: 'Joined', color: chartColor('--teal') },
                      ])}
                      yAxis={COUNT_Y_AXIS}
                      height={280}
                      margin={{ top: 20, right: 16, bottom: 48, left: 44 }}
                    />
                  ) : (
                    <EmptyState compact title="No trend data for this period" />
                  )}
                </CardContent>
              </Card>
            </Box>
          </>
        )}

        {/* ── Pipeline Health ──────────────────────────────────────────── */}
        {tab === 'pipeline' && pipeline && (
          <>
            <Box className="cc-kpi-grid">
              <KpiTile label="Open Roles" value={num(pipeline.openJobs)} icon={<WorkOutlineOutlinedIcon />} tone="primary" />
              <KpiTile label="Filled Roles" value={num(pipeline.filledJobs)} icon={<GroupsOutlinedIcon />} tone="success" />
              <KpiTile label="Zero Applicants" value={num(pipeline.jobsWithZeroApplicants)} icon={<WorkOutlineOutlinedIcon />} tone={pipeline.jobsWithZeroApplicants > 0 ? 'danger' : 'teal'} />
              <KpiTile label="Open > 30 days" value={num(pipeline.rolesOpenOver30Days)} icon={<HourglassBottomOutlinedIcon />} tone={pipeline.rolesOpenOver30Days > 0 ? 'amber' : 'teal'} />
              <KpiTile label="Stuck Candidates" value={num(pipeline.stuckCandidates)} icon={<HourglassBottomOutlinedIcon />} tone={pipeline.stuckCandidates > 0 ? 'danger' : 'teal'} />
            </Box>

            <Card variant="outlined" className="cc-panel cc-panel--panel an-panel">
              <CardContent>
                <Typography variant="subtitle1" className="an-chart-title">Candidates by stage</Typography>
                {pipeline.stageCounts.some((s) => s.count > 0) ? (
                  <BarChart
                    hideLegend
                    xAxis={[{ data: pipeline.stageCounts.map((s) => s.stage), scaleType: 'band', tickLabelStyle: { fontSize: 11 } }]}
                    series={[{ data: pipeline.stageCounts.map((s) => s.count), label: 'Candidates', color: chartColor('--indigo') }]}
                    height={300}
                    margin={{ top: 20, right: 16, bottom: 48, left: 44 }}
                  />
                ) : (
                  <EmptyState compact title="No candidates currently in the pipeline" />
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* ── Recruiter Productivity ───────────────────────────────────── */}
        {tab === 'recruiter' && recruiters && (
          <>
            {recruiters.length > 0 && teamView && (
              <Card variant="outlined" className="cc-panel cc-panel--panel an-panel">
                <CardContent>
                  <Typography variant="subtitle1" className="an-chart-title">Hires by recruiter</Typography>
                  <BarChart
                    hideLegend
                    xAxis={[{ data: recruiters.slice(0, 10).map((r) => recruiterName(r.recruiterId)), scaleType: 'band', tickLabelStyle: { fontSize: 11 } }]}
                    series={[{ data: recruiters.slice(0, 10).map((r) => r.hires), label: 'Hires', color: chartColor('--green') }]}
                    height={260}
                    margin={{ top: 20, right: 16, bottom: 64, left: 40 }}
                  />
                </CardContent>
              </Card>
            )}

            <Card variant="outlined" className="cc-panel cc-panel--panel an-panel">
              <CardContent>
                <Typography variant="subtitle1" className="an-chart-title">
                  {platformWide ? 'Recruiter leaderboard' : 'Your scorecard'}
                </Typography>
                <MetricTable<RecruiterStats>
                  rows={recruiters}
                  getRowKey={(r) => r.recruiterId}
                  // "for this period" sent a reviewer looking for a date range
                  // to widen, when the real precondition is upstream: nothing
                  // appears here until an HR is assigned to a managed job and
                  // its applications start moving. Naming that is the
                  // difference between an empty screen and a broken one.
                  emptyLabel={
                    teamView
                      ? 'No recruiter activity yet. Numbers appear once HRs are assigned to Premium Hiring jobs on the HR page and applications move through the pipeline.'
                      : 'No recruiter activity yet. Your numbers appear once you are assigned to a Premium Hiring job and its applications start moving.'
                  }
                  defaultSort={{ key: 'hires', dir: 'desc' }}
                  columns={[
                    { key: 'rank', header: '#', variant: 'rank', render: (_r, i) => i + 1 },
                    { key: 'name', header: 'Recruiter', variant: 'name',
                      render: (r) => recruiterName(r.recruiterId), sortValue: (r) => recruiterName(r.recruiterId) },
                    { key: 'jobsHandled', header: 'Jobs handled',
                      tooltip: 'Premium Hiring jobs assigned to this recruiter: their live workload, independent of candidate volume.',
                      render: (r) => num(r.jobsHandled), sortValue: (r) => r.jobsHandled },
                    { key: 'candidatesHandled', header: 'Candidates',
                      render: (r) => num(r.candidatesHandled), sortValue: (r) => r.candidatesHandled },
                    { key: 'interviews', header: 'Interviews',
                      render: (r) => num(r.interviews), sortValue: (r) => r.interviews },
                    { key: 'offersMade', header: 'Offers',
                      render: (r) => num(r.offersMade), sortValue: (r) => r.offersMade },
                    { key: 'hires', header: 'Hires', variant: 'strong',
                      render: (r) => num(r.hires), sortValue: (r) => r.hires },
                    { key: 'creditedHires', header: 'Credited',
                      tooltip: 'Weighted hire credit to the recruiter who marked the hire: FULL_TIME 1.0, otherwise 0.5. Counts only on a Joined.',
                      render: (r) => (r.creditedHires != null ? r.creditedHires.toFixed(1) : '-'),
                      sortValue: (r) => r.creditedHires ?? 0 },
                    { key: 'offerToJoinRate', header: 'Offer→Join',
                      render: (r) => pct(r.offerToJoinRate), sortValue: (r) => r.offerToJoinRate },
                    { key: 'avgDaysToShortlist', header: 'Days→Shortlist',
                      render: (r) => (r.avgDaysToShortlist ? r.avgDaysToShortlist.toFixed(1) : '-'),
                      sortValue: (r) => r.avgDaysToShortlist ?? 0 },
                    { key: 'avgDaysToHire', header: 'Days→Hire',
                      render: (r) => (r.avgDaysToHire ? r.avgDaysToHire.toFixed(1) : '-'),
                      sortValue: (r) => r.avgDaysToHire ?? 0 },
                  ]}
                />
              </CardContent>
            </Card>
          </>
        )}

        {/* ── Job Performance ──────────────────────────────────────────── */}
        {tab === 'submissions' && submissions && (
          <>
            <Card variant="outlined" className="cc-panel cc-panel--panel an-panel">
              <CardContent>
                <Box className="an-submissions-head">
                  <Typography variant="subtitle1" className="an-chart-title">
                    {platformWide ? 'Submissions by recruiter' : 'Your submissions'}
                  </Typography>
                  {/*
                    * A tab-local day range. PERIODS starts at 30 days on a week granularity, and
                    * this matrix is always daily -- 30 columns is already wide, 90 is unreadable.
                    */}
                  <CRMTabs
                    variant="inline"
                    tabs={[{ label: '7 days' }, { label: '14 days' }, { label: '30 days' }]}
                    value={[7, 14, 30].indexOf(submissionDays)}
                    onChange={(i) => setSubmissionDays([7, 14, 30][i])}
                  />
                </Box>

                <MetricTable<SubmissionRow>
                  rows={submissions.rows}
                  getRowKey={(r) => r.recruiterId}
                  emptyLabel={
                    teamView
                      ? 'No submissions in this window. A submission is recorded when an application moves to Submitted or Resubmitted.'
                      : 'No submissions from you in this window. One is recorded when you move an application to Submitted or Resubmitted.'
                  }
                  defaultSort={{ key: 'total', dir: 'desc' }}
                  columns={[
                    {
                      key: 'name',
                      header: 'Recruiter',
                      variant: 'name',
                      render: (r) => recruiterName(r.recruiterId),
                      sortValue: (r) => recruiterName(r.recruiterId),
                    },
                    {
                      key: 'total',
                      header: 'Total',
                      variant: 'strong',
                      render: (r) => num(r.total),
                      sortValue: (r) => r.total,
                    },
                    ...submissions.days.map((day) => ({
                      key: day,
                      header: day.slice(5),
                      tooltip: day,
                      render: (r: SubmissionRow) => {
                        const cell = r.byDay.find((d) => d.day === day);
                        const count = cell?.count ?? 0;
                        if (!count) return <span className="an-cell-zero">-</span>;
                        const selected = drillHr === r.recruiterId && drillDay === day;
                        return (
                          <button
                            type="button"
                            className={`an-cell-link${selected ? ' an-cell-link--selected' : ''}`}
                            onClick={() => selectCell(r.recruiterId, day)}
                          >
                            {num(count)}
                          </button>
                        );
                      },
                    })),
                  ]}
                />

                <Typography variant="body2" className="an-footnote">
                  {num(submissions.totalSubmissions)} submissions attributed
                  {submissions.unattributedSubmissions > 0 && (
                    <>
                      {' · '}
                      {num(submissions.unattributedSubmissions)} not attributed to a recruiter
                      (self-applied candidates, automated transitions, or a legacy actor the user
                      directory cannot place)
                    </>
                  )}
                  {' · '}window {submissions.from} to {submissions.to}
                </Typography>
              </CardContent>
            </Card>

            {/*
              * The drill-down renders BELOW the matrix, on the same page. Not a modal: a record
              * detail in a dialog is the pattern this console removed everywhere else, and each
              * candidate here links out to its own workspace rather than reproducing it.
              */}
            {drillHr && drillDay && (
              <Card variant="outlined" className="cc-panel cc-panel--panel an-panel">
                <CardContent>
                  <Typography variant="subtitle1" className="an-chart-title">
                    {recruiterName(drillHr)} · {drillDay}
                  </Typography>
                  {/* Records (a candidate each, linking to its application), so MintlyTable:
                      a card per candidate on a phone. MetricTable is for comparing numbers down a
                      column, and this table has none. */}
                  <MintlyTable
                    loading={drillLoading || !drillRows}
                    rows={drillRows ?? []}
                    getRowKey={(r: SubmissionRecord) => r.applicationId}
                    emptyLabel="No candidates for this day."
                    selectors={['candidate', 'job', 'company', 'status', 'kind']}
                    columns={{
                      candidate: { card: 'title', tablet: true },
                      job: { card: 'subtitle', tablet: true },
                      status: { card: 'status', tablet: true },
                    }}
                    tableHeadRow={{ candidate: 'Candidate', job: 'Role', company: 'Client', status: 'Status now', kind: 'Kind' }}
                    tableBodyRow={{
                      candidate: (r: SubmissionRecord) => (
                        <Link className="cc-link" to={`/job-applications/${r.applicationId}`}>
                          {r.candidateName || r.jobSeekerId}
                        </Link>
                      ),
                      job: (r: SubmissionRecord) => <>{r.jobTitle || '-'}</>,
                      company: (r: SubmissionRecord) => <>{r.companyName || '-'}</>,
                      // The CURRENT status, which may be well past Submitted by now.
                      status: (r: SubmissionRecord) => (
                        <CRMStatusChip
                          status={r.status}
                          kind="application"
                          label={statusSummary(r.status, (r as { subStatus?: string }).subStatus)}
                        />
                      ),
                      kind: (r: SubmissionRecord) => <>{r.resubmission ? 'Resubmitted' : 'Submitted'}</>,
                    }}
                  />
                </CardContent>
              </Card>
            )}
          </>
        )}

        {tab === 'jobs' && jobsReport && (
          <>
            <Box className="cc-kpi-grid">
              <KpiTile label="Total Views" value={num(jobsReport.totalViews)} icon={<VisibilityOutlinedIcon />} tone="primary" />
              <KpiTile label="Applications" value={num(jobsReport.totalApplications)} icon={<PeopleAltOutlinedIcon />} tone="indigo" />
              <KpiTile label="Avg Apply Rate" value={pct(jobsReport.avgApplyRate)} icon={<TrendingUpOutlinedIcon />} tone="teal" />
              <KpiTile label="Avg Days to 1st Applicant" value={jobsReport.avgDaysToFirstApplicant ? jobsReport.avgDaysToFirstApplicant.toFixed(1) : '-'} icon={<HourglassBottomOutlinedIcon />} tone="amber" />
              <KpiTile label="Jobs Covered" value={num(jobsReport.jobsCovered)} icon={<WorkOutlineOutlinedIcon />} tone="success" />
            </Box>

            {jobsReport.jobs.length > 0 && (
              <Card variant="outlined" className="cc-panel cc-panel--panel an-panel">
                <CardContent>
                  <Typography variant="subtitle1" className="an-chart-title">Apply rate by job (top 10 by applications)</Typography>
                  <BarChart
                    hideLegend
                    xAxis={[{ data: jobsReport.jobs.slice(0, 10).map((j) => j.title || j.jobId.slice(-6)), scaleType: 'band', tickLabelStyle: { fontSize: 11, angle: -25, textAnchor: 'end' } }]}
                    series={[{ data: jobsReport.jobs.slice(0, 10).map((j) => j.applyRate), label: 'Apply rate %', color: chartColor('--acc') }]}
                    height={280}
                    margin={{ top: 20, right: 16, bottom: 90, left: 44 }}
                  />
                </CardContent>
              </Card>
            )}

            <Card variant="outlined" className="cc-panel cc-panel--panel an-panel">
              <CardContent>
                <Typography variant="subtitle1" className="an-chart-title">Per-job breakdown</Typography>
                <MetricTable<JobPerf>
                  rows={jobsReport.jobs}
                  getRowKey={(j) => j.jobId}
                  emptyLabel="No jobs posted in this period."
                  columns={[
                    { key: 'name', header: 'Job', variant: 'name',
                      render: (j) => (
                        <span title={j.title || j.jobId}>
                          {j.title || j.jobId.slice(-6)}
                          {j.companyName ? <span className="mt-sub">{j.companyName}</span> : null}
                        </span>
                      ),
                      sortValue: (j) => j.title || j.jobId },
                    { key: 'views', header: 'Views', render: (j) => num(j.views), sortValue: (j) => j.views },
                    { key: 'applications', header: 'Applies', render: (j) => num(j.applications), sortValue: (j) => j.applications },
                    { key: 'applyRate', header: 'Apply %', variant: 'strong',
                      render: (j) => pct(j.applyRate), sortValue: (j) => j.applyRate },
                    { key: 'shortlistRate', header: 'Shortlist %', render: (j) => pct(j.shortlistRate), sortValue: (j) => j.shortlistRate },
                    { key: 'dropOffRate', header: 'Drop-off %', render: (j) => pct(j.dropOffRate), sortValue: (j) => j.dropOffRate },
                    { key: 'hires', header: 'Hires', render: (j) => num(j.hires), sortValue: (j) => j.hires },
                    { key: 'daysToFirstApplicant', header: 'Days→1st',
                      render: (j) => (j.daysToFirstApplicant >= 0 ? j.daysToFirstApplicant.toFixed(1) : '-'),
                      sortValue: (j) => j.daysToFirstApplicant },
                  ]}
                />
              </CardContent>
            </Card>
          </>
        )}

        {/* ── Candidate Quality ────────────────────────────────────────── */}
        {tab === 'quality' && quality && (
          <>
            <Box className="cc-kpi-grid">
              <KpiTile label="Candidates" value={num(quality.totalCandidates)} icon={<PeopleAltOutlinedIcon />} tone="indigo" />
              <KpiTile label="Interviewed" value={num(quality.interviewed)} icon={<GroupsOutlinedIcon />} tone="teal" />
              <KpiTile label="Hired" value={num(quality.hired)} icon={<GroupsOutlinedIcon />} tone="success" />
              <KpiTile label="Interview → Hire" value={pct(quality.interviewToHireRate)} icon={<TrendingUpOutlinedIcon />} tone="primary" />
              <KpiTile label="Strong AI matches" value={`${num(quality.strongMatchCandidates)} / ${num(quality.evaluatedCandidates)}`} icon={<WorkspacePremiumOutlinedIcon />} tone="amber" />
              <KpiTile label="Avg AI match score" value={quality.evaluatedCandidates > 0 ? quality.avgMatchScore.toFixed(0) : '-'} icon={<TrendingUpOutlinedIcon />} tone="teal" />
              <KpiTile label="Interview no-show" value={quality.interviewsRecorded > 0 ? pct(quality.interviewNoShowRate) : '-'} icon={<HourglassBottomOutlinedIcon />} tone={quality.interviewNoShowRate >= 30 ? 'danger' : 'teal'} />
            </Box>
            {quality.evaluatedCandidates === 0 && (
              <Typography variant="caption" className="an-note">
                AI match scores appear here once the recommendation engine has scored applicants for these jobs.
              </Typography>
            )}

            <FilterRow>
              <Typography variant="body2" sx={{ color: 'var(--tx-mid)', alignSelf: 'center' }}>Segment by</Typography>
              <ToggleButtonGroup
                value={segmentBy}
                exclusive
                size="small"
                onChange={(_, v) => { if (v !== null) setSegmentBy(v); }}
              >
                <ToggleButton value="industry">Industry</ToggleButton>
                <ToggleButton value="experience">Experience</ToggleButton>
                <ToggleButton value="city">Location</ToggleButton>
              </ToggleButtonGroup>
            </FilterRow>

            <Box className="an-charts-row">
              <Card variant="outlined" className="cc-panel cc-panel--panel an-panel an-chart-half">
                <CardContent>
                  <Typography variant="subtitle1" className="an-chart-title">Interview → hire by segment</Typography>
                  {quality.segments.length > 0 ? (
                    <BarChart
                      hideLegend
                      xAxis={[{ data: quality.segments.slice(0, 12).map((s) => s.segment), scaleType: 'band', tickLabelStyle: { fontSize: 11, angle: -25, textAnchor: 'end' } }]}
                      series={[{ data: quality.segments.slice(0, 12).map((s) => s.interviewToHireRate), label: 'Interview→Hire %', color: chartColor('--teal') }]}
                      height={280}
                      margin={{ top: 20, right: 16, bottom: 80, left: 44 }}
                    />
                  ) : (
                    <EmptyState compact title="No candidate data for this period" />
                  )}
                </CardContent>
              </Card>

              <Card variant="outlined" className="cc-panel cc-panel--panel an-panel an-chart-half">
                <CardContent>
                  <Typography variant="subtitle1" className="an-chart-title">Why candidates were closed</Typography>
                  {quality.rejectionReasons.length > 0 ? (
                    <PieChart
                      series={[{
                        data: quality.rejectionReasons.map((r, i) => ({ id: i, value: r.count, label: r.reason })),
                        innerRadius: 50,
                        outerRadius: 100,
                        paddingAngle: 3,
                        cornerRadius: 4,
                      }]}
                      height={240}
                      slotProps={{ legend: { direction: 'vertical', position: { vertical: 'middle', horizontal: 'end' } } }}
                    />
                  ) : (
                    <EmptyState compact title="No rejections recorded" />
                  )}
                </CardContent>
              </Card>
            </Box>

            <Card variant="outlined" className="cc-panel cc-panel--panel an-panel">
              <CardContent>
                <Typography variant="subtitle1" className="an-chart-title">Quality by segment</Typography>
                <MetricTable<SegmentQuality>
                  rows={quality.segments}
                  getRowKey={(s) => s.segment}
                  emptyLabel="No candidate data for this period."
                  columns={[
                    { key: 'segment', header: 'Segment', variant: 'name',
                      render: (s) => s.segment, sortValue: (s) => s.segment },
                    { key: 'candidates', header: 'Candidates', render: (s) => num(s.candidates), sortValue: (s) => s.candidates },
                    { key: 'interviews', header: 'Interviews', render: (s) => num(s.interviews), sortValue: (s) => s.interviews },
                    { key: 'hires', header: 'Hires', render: (s) => num(s.hires), sortValue: (s) => s.hires },
                    { key: 'interviewToHireRate', header: 'Interview→Hire', variant: 'strong',
                      render: (s) => pct(s.interviewToHireRate), sortValue: (s) => s.interviewToHireRate },
                    { key: 'shortlistRate', header: 'Shortlist %', render: (s) => pct(s.shortlistRate), sortValue: (s) => s.shortlistRate },
                  ]}
                />
              </CardContent>
            </Card>
          </>
        )}

        {/* ── Revenue / ROI (Super Admin only) ─────────────────────────── */}
        {tab === 'revenue' && revenue && (
          <>
            {superAdmin ? (
              <>
                <Box className="cc-kpi-grid">
                  <KpiTile label={`Revenue (${revenue.currency || '-'})`} value={fmtMoney(revenue.totalRevenue, revenue.currency)} icon={<PaidOutlinedIcon />} tone="success" />
                  <KpiTile label={`Placement fees (${num(revenue.placements ?? 0)})`} value={fmtMoney(revenue.placementRevenue ?? 0, revenue.currency)} icon={<HandshakeOutlinedIcon />} tone="teal" />
                  <KpiTile label="Plan purchases" value={fmtMoney(revenue.planRevenue ?? 0, revenue.currency)} icon={<ReceiptLongOutlinedIcon />} tone="primary" />
                  <KpiTile label="Transactions" value={num(revenue.transactions)} icon={<ReceiptLongOutlinedIcon />} tone="primary" />
                  <KpiTile label="Paying Employers" value={num(revenue.payingEmployers)} icon={<StorefrontOutlinedIcon />} tone="indigo" />
                  <KpiTile label="Free → Paid" value={pct(revenue.freeToPaidRate)} icon={<TrendingUpOutlinedIcon />} tone="teal" />
                </Box>
              </>
            ) : (
              <>
                {/* An HR earns the business placement fees: one month's or half a month's salary for
                    each candidate they place who joins. Plan purchases are not theirs to bring in, so
                    they are not on this view. */}
                <Box className="cc-kpi-grid">
                  <KpiTile label="Your placement fees" value={fmtMoney(revenue.placementRevenue ?? 0, revenue.currency)} icon={<HandshakeOutlinedIcon />} tone="success" />
                  <KpiTile label="Candidates placed" value={num(revenue.placements ?? 0)} icon={<PaidOutlinedIcon />} tone="teal" />
                  <KpiTile
                    label="Average fee"
                    value={(revenue.placements ?? 0) > 0 ? fmtMoney((revenue.placementRevenue ?? 0) / (revenue.placements ?? 1), revenue.currency) : '-'}
                    icon={<TrendingUpOutlinedIcon />}
                    tone="primary"
                  />
                </Box>
              </>
            )}

            {revenue.availableCurrencies.length > 1 && (
              <FilterRow>
                <Typography variant="body2" sx={{ color: 'var(--tx-mid)', alignSelf: 'center' }}>Currency</Typography>
                {/* The state, not the response. Binding to `revenue.currency` meant the toggle
                    only moved once the refetch came back, so a click looked like it had missed
                    for as long as the query took. `currency` is adopted from the response on
                    first load, so it is never empty by the time this renders. */}
                <ToggleButtonGroup
                  value={currency || revenue.currency}
                  exclusive
                  size="small"
                  onChange={(_, v) => { if (v !== null) setCurrency(v); }}
                >
                  {revenue.availableCurrencies.map((c) => (
                    <ToggleButton key={c} value={c}>{c}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </FilterRow>
            )}

            {superAdmin && (
              <Card variant="outlined" className="cc-panel cc-panel--panel an-panel">
                <CardContent>
                  <Typography variant="subtitle1" className="an-chart-title">Placement fees by HR</Typography>
                  <MetricTable<HrRevenueRow>
                    rows={revenue.byHr ?? []}
                    emptyLabel="No revenue recorded against any HR yet."
                    getRowKey={(r) => r.hrUserId}
                    defaultSort={{ key: 'fees', dir: 'desc' }}
                    columns={[
                      { key: 'hr', header: 'HR', variant: 'name',
                        render: (r) => (r.hrUserId === 'UNASSIGNED' ? 'No HR assigned' : recruiterName(r.hrUserId)),
                        sortValue: (r) => recruiterName(r.hrUserId) },
                      { key: 'placements', header: 'Placements', render: (r) => num(r.placements), sortValue: (r) => r.placements },
                      { key: 'fees', header: 'Placement fees', variant: 'strong', render: (r) => fmtMoney(r.placementRevenue, revenue.currency), sortValue: (r) => r.placementRevenue },
                      { key: 'plans', header: "Their clients' plans", render: (r) => fmtMoney(r.planRevenue, revenue.currency), sortValue: (r) => r.planRevenue },
                      { key: 'total', header: 'Total', render: (r) => fmtMoney(r.total, revenue.currency), sortValue: (r) => r.total },
                    ]}
                  />
                </CardContent>
              </Card>
            )}


            <Card variant="outlined" className="cc-panel cc-panel--panel an-panel">
              <CardContent>
                <Typography variant="subtitle1" className="an-chart-title">
                  {superAdmin ? 'Recent placement fees' : 'Your recent placement fees'}
                </Typography>
                <MetricTable<PlacementFeeRow>
                  rows={fees}
                  getRowKey={(r) => r._id}
                  emptyLabel="No placement fees recorded yet. A Super Admin records one on a hired application."
                  defaultSort={{ key: 'hired', dir: 'desc' }}
                  columns={[
                    { key: 'job', header: 'Job', variant: 'name', render: (r) => r.jobTitle || '-', sortValue: (r) => r.jobTitle || '' },
                    { key: 'hired', header: 'Joined', render: (r) => formatDate(r.hiredAt), sortValue: (r) => r.hiredAt || '' },
                    ...(superAdmin
                      ? [{ key: 'hr', header: 'HR', render: (r: PlacementFeeRow) => (r.hrUserId ? recruiterName(r.hrUserId) : 'No HR'), sortValue: (r: PlacementFeeRow) => recruiterName(r.hrUserId || '') }]
                      : []),
                    { key: 'basis', header: 'Terms', render: (r) => feeTerms(r) },
                    { key: 'amount', header: 'Fee', variant: 'strong', render: (r) => fmtMoney(r.amount, r.currency), sortValue: (r) => r.amount },
                  ]}
                />
              </CardContent>
            </Card>

            {superAdmin && (
              <>
                <Box className="an-charts-row">
                  <Card variant="outlined" className="cc-panel cc-panel--panel an-panel an-chart-half">
                    <CardContent>
                      <Typography variant="subtitle1" className="an-chart-title">Revenue over time</Typography>
                      {revenue.trend.length > 0 ? (
                        <LineChart
                          hideLegend
                          xAxis={[{ data: revenue.trend.map((t) => t.period), scaleType: 'band', tickLabelStyle: { fontSize: 11 } }]}
                          // Compact axis labels (8L, 1.2Cr): full figures were cut to "800,..." at 60px.
                          yAxis={[{ valueFormatter: (v: number | null) => compactAmount(v, revenue.currency) }]}
                          series={[{ data: revenue.trend.map((t) => t.revenue), label: `Revenue (${revenue.currency})`, color: chartColor('--green'), area: true, valueFormatter: (v: number | null) => fmtMoney(v ?? 0, revenue.currency) }]}
                          height={280}
                          margin={{ top: 20, right: 16, bottom: 48, left: 60 }}
                        />
                      ) : (
                        <EmptyState compact title="No revenue in this period" />
                      )}
                    </CardContent>
                  </Card>

                  <Card variant="outlined" className="cc-panel cc-panel--panel an-panel an-chart-half">
                    <CardContent>
                      <Typography variant="subtitle1" className="an-chart-title">Revenue by plan</Typography>
                      {revenue.byPlan.length > 0 ? (
                        <PieChart
                          series={[{
                            data: revenue.byPlan.map((p, i) => ({ id: i, value: p.revenue, label: p.planType })),
                            innerRadius: 50,
                            outerRadius: 100,
                            paddingAngle: 3,
                            cornerRadius: 4,
                          }]}
                          height={240}
                          slotProps={{ legend: { direction: 'vertical', position: { vertical: 'middle', horizontal: 'end' } } }}
                        />
                      ) : (
                        <EmptyState compact title="No plan revenue in this period" />
                      )}
                    </CardContent>
                  </Card>
                </Box>


                <Card variant="outlined" className="cc-panel cc-panel--panel an-panel">
                  <CardContent>
                    <Typography variant="subtitle1" className="an-chart-title">Top employers by revenue (lifetime-value proxy)</Typography>
                    <MetricTable<RevenueReport['topEmployers'][number]>
                      rows={revenue.topEmployers}
                      getRowKey={(e, i) => e.employerId || String(i)}
                      emptyLabel="No employer revenue in this period."
                      defaultSort={{ key: 'revenue', dir: 'desc' }}
                      columns={[
                        { key: 'rank', header: '#', variant: 'rank', render: (_e, i) => i + 1 },
                        { key: 'name', header: 'Employer', variant: 'name',
                          render: (e) => e.companyName || e.employerId || 'Unknown',
                          sortValue: (e) => e.companyName || e.employerId || 'Unknown' },
                        { key: 'revenue', header: `Revenue (${revenue.currency ?? ''})`, variant: 'strong',
                          render: (e) => fmtMoney(e.revenue, revenue.currency), sortValue: (e) => e.revenue },
                      ]}
                    />
                  </CardContent>
                </Card>

              </>
            )}

            <Typography variant="caption" className="an-note">
              {superAdmin
                ? 'Revenue is plan purchases plus placement fees. A placement fee is one month or half a month of the salary of a candidate who joined, credited to the HR who placed them.'
                : 'A placement fee is one month or half a month of the salary of a candidate you placed who joined. A Super Admin records it on the application.'}
            </Typography>
          </>
        )}

        {/* ── Alerts & Risks ───────────────────────────────────────────── */}
        {/* ── Email ───────────────────────────────────────────────────
            The existing EmailAnalytics panel, unchanged, rehoused. It was a tab in Email
            Operations; this page is where the platform's other cross-cutting numbers live. */}
        {/* EmailAnalytics brings its own PageHero (period toggle, refresh); the slot says it in
            this page's header instead of as a second header under the tabs. */}
        {tab === 'email' && (
          <PageHeroSlot slots={headerSlots} active>
            <EmailAnalytics />
          </PageHeroSlot>
        )}
        {tab === 'cities' && <CandidatesByCity />}
        {tab === 'activity' && <ActivityMonitor />}
        {tab === 'referrals' && <ReferralInsights />}

        {tab === 'alerts' && pipeline && (
          <>
            <Box className="an-alert-banner">
              <NotificationsActiveOutlinedIcon />
              <Typography variant="body2">
                {superAdmin
                  ? 'Risks are checked every week and pushed to your team’s notification bell. Below is the current live snapshot across the platform.'
                  : 'Risks across your assigned accounts are checked weekly and pushed to your notification bell. Below is the current live snapshot.'}
              </Typography>
            </Box>

            <Box className="cc-kpi-grid">
              {[
                { label: 'Open roles with no applicants', value: pipeline.jobsWithZeroApplicants, tone: 'danger' as const },
                { label: 'Roles open over 30 days', value: pipeline.rolesOpenOver30Days, tone: 'amber' as const },
                { label: 'Candidates stuck over 5 days', value: pipeline.stuckCandidates, tone: 'danger' as const },
              ].map((r) => {
                const active = r.value > 0;
                return (
                  <KpiTile
                    key={r.label}
                    label={r.label}
                    value={num(r.value)}
                    icon={active ? <WarningAmberOutlinedIcon /> : <CheckCircleOutlineOutlinedIcon />}
                    tone={active ? r.tone : 'success'}
                  />
                );
              })}
            </Box>

            {pipeline.jobsWithZeroApplicants === 0 &&
              pipeline.rolesOpenOver30Days === 0 &&
              pipeline.stuckCandidates === 0 && (
                <EmptyState compact title="No active risks right now. The pipeline is healthy" />
              )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default Analytics;
